// src/features/insights/BreakoutInsightsPage.tsx
//
// The Momentum Insights dashboard — wired to the "Insights" entry under the
// Knowledge section of the sidebar. Renders the two pre-ranked breakout
// leaderboards (top buys / top sells) served off the composite DB index.
//
// Reuses the shared <Layout sidebar={<Sidebar />}> shell like every other
// authenticated feature page. Access is gated at the routing layer by
// <ProtectedRoute> in App.tsx (Premium / Professional / administrator only).

import { useEffect, useState } from 'react';

import { CalendarDays, ExternalLink, Flame, Users } from 'lucide-react';

import { Layout, Sidebar } from '../../shared/ui';
import { EarningsWidget } from '../earnings-calendar/components/EarningsWidget';
import { TopPerformersWidget } from '../top-performers';
import {
    fetchBreakoutInsights,
    fetchCryptoInsights,
    type BreakoutInsightsResponse,
    type CryptoInsightsResponse,
} from './api';
import { InsightTable } from './components/InsightTable';
import { CryptoInsightTable } from './components/CryptoInsightTable';

// In-page navigation targets. Each entry maps a sticky tab to the anchored
// section further down the page. `id` MUST match the `id` attribute set on the
// corresponding section divider so `scrollIntoView` + the IntersectionObserver
// can resolve the element. `activeClass` carries the per-tab gradient/glow so
// the highlighted tab visually echoes the colour of its section separator
// (indigo = Earnings Radar, amber/orange = Top Performers).
const NAV_SECTIONS = [
    {
        id: 'earnings-radar',
        label: 'Earnings Radar',
        icon: CalendarDays,
        activeClass:
            'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-500/40 ring-1 ring-inset ring-white/20',
    },
    {
        id: 'top-performers',
        label: 'Forge Ultra Top Performers',
        icon: Flame,
        activeClass:
            'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/40 ring-1 ring-inset ring-white/20',
    },
] as const;

export const BreakoutInsightsPage = () => {
    const [data, setData] = useState<BreakoutInsightsResponse | null>(null);
    const [cryptoData, setCryptoData] = useState<CryptoInsightsResponse | null>(
        null,
    );
    const [isLoading, setIsLoading] = useState(true);
    const [activeSection, setActiveSection] = useState<string>(
        NAV_SECTIONS[0].id,
    );

    // Smoothly scroll the chosen section into view and optimistically light up
    // its tab (the observer below will keep it accurate as the user scrolls).
    const handleScrollTo = (id: string) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveSection(id);
        }
    };

    // Highlight the tab whose section is currently centred in the viewport.
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            // Fire when a section crosses the vertical middle band of the screen.
            { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
        );

        NAV_SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        let isCancelled = false;
        setIsLoading(true);

        // Fetch BOTH APIs concurrently.
        Promise.all([fetchBreakoutInsights(10), fetchCryptoInsights()]).then(
            ([equitiesRes, cryptoRes]) => {
                if (isCancelled) return;
                setData(equitiesRes);
                setCryptoData(cryptoRes);
                setIsLoading(false);
            },
        );

        return () => {
            isCancelled = true;
        };
    }, []);

    return (
        <Layout sidebar={<Sidebar />} title="Momentum Insights">
            {/* --- Sticky in-page navigation tabs -------------------------- */}
            {/* Floats at the top of the scroll area so the user can jump to the
                Earnings Radar / Top Performers sections at any scroll depth. The
                active tab is driven by the IntersectionObserver above. */}
            <nav className="sticky top-0 z-30 mb-8 flex justify-center">
                <div className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200/80 bg-white/75 p-1.5 shadow-xl shadow-slate-900/5 backdrop-blur-md dark:border-slate-700/60 dark:bg-slate-900/70">
                    <span className="hidden sm:inline pl-2 pr-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
                        Jump&nbsp;to
                    </span>
                    {NAV_SECTIONS.map(({ id, label, icon: Icon, activeClass }) => {
                        const isActive = activeSection === id;
                        return (
                            <button
                                key={id}
                                type="button"
                                onClick={() => handleScrollTo(id)}
                                aria-current={isActive ? 'true' : undefined}
                                className={`group relative flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-bold transition-all duration-300 sm:px-4 ${
                                    isActive
                                        ? activeClass
                                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
                                }`}
                            >
                                <Icon
                                    className={`h-4 w-4 transition-transform duration-300 ${
                                        isActive
                                            ? 'scale-110'
                                            : 'group-hover:scale-110'
                                    }`}
                                />
                                <span className="whitespace-nowrap">{label}</span>
                            </button>
                        );
                    })}
                    <a
                        href="/dashboard/insider-trading"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-600 transition-all duration-300 hover:bg-gradient-to-r hover:from-emerald-500 hover:to-teal-500 hover:text-white hover:shadow-lg hover:shadow-emerald-500/40 dark:text-slate-300 sm:px-4"
                    >
                        <Users className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
                        <span className="whitespace-nowrap">Insider Trading</span>
                        <ExternalLink className="h-3 w-3 opacity-60" />
                    </a>
                </div>
            </nav>

            {/* Page header + performance metric */}
            <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 dark:border-slate-700 pb-6">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                        Daily Quantitative Actionable Insights for Stocks and ETFs
                    </h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-3xl leading-relaxed">
                        Algorithmic discovery of institutional volume footprints.
                        These assets have decisively crossed major 20-day technical
                        resistance or support thresholds with extreme conviction.
                    </p>
                </div>

                {data && (
                    <div className="text-[10px] font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2.5 py-1.5 rounded border border-emerald-200 dark:border-emerald-800/50 flex items-center gap-2 shadow-sm whitespace-nowrap">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                        DB Scan: {data.execution_time_ms.toFixed(2)}ms
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="h-96 flex flex-col items-center justify-center text-slate-500 dark:text-slate-400">
                    <div className="w-8 h-8 border-4 border-slate-200 dark:border-slate-700 border-t-blue-500 rounded-full animate-spin mb-4" />
                    <span className="text-sm font-bold tracking-widest uppercase">
                        Scanning Database &amp; Redis cache…
                    </span>
                </div>
            ) : !data && !cryptoData ? (
                <div className="h-48 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-sm text-red-700 dark:text-red-400">
                    Failed to load quantitative insights. Please try again later.
                </div>
            ) : (
                <div className="space-y-12 pb-12">
                    {/* TradFi section (Equities & ETFs) */}
                    {data && (
                        <div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 pl-2 border-l-4 border-blue-500">
                                US &amp; EU Equities
                                <span className="text-sm font-normal text-slate-500 ml-2">
                                    (Mid &amp; Large Caps · High Liquidity · Volume Surge)
                                </span>
                            </h2>
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8 items-stretch">
                                <InsightTable
                                    title="Top Breakouts"
                                    items={data.top_buys}
                                    theme="buy"
                                />
                                <InsightTable
                                    title="Top Breakdowns"
                                    items={data.top_sells}
                                    theme="sell"
                                />
                            </div>
                        </div>
                    )}

                    {/* Digital Assets section */}
                    {cryptoData && (
                        <div>
                            <div className="flex items-end justify-between mb-6">
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white pl-2 border-l-4 border-orange-500">
                                    Digital Assets
                                    <span className="text-sm font-normal text-slate-500 ml-2">
                                        (Trend &amp; RSI)
                                    </span>
                                </h2>
                                {cryptoData.updated_at && (
                                    <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                                        Cache:{' '}
                                        {new Date(
                                            cryptoData.updated_at,
                                        ).toLocaleTimeString()}
                                    </span>
                                )}
                            </div>
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8 items-stretch">
                                <CryptoInsightTable
                                    title="Top Crypto Setups"
                                    items={cryptoData.top_buys}
                                    theme="buy"
                                />
                                <CryptoInsightTable
                                    title="Top Crypto Weakness"
                                    items={cryptoData.top_sells}
                                    theme="sell"
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* --- Earnings Radar: eye-catching separator + widget --------- */}
            {/* Rendered OUTSIDE the breakout loading/error ternary so it owns
                its own TanStack Query state and appears even while the momentum
                leaderboards above are still loading. */}
            <div
                id="earnings-radar"
                className="mt-16 mb-10 flex items-center gap-4 scroll-mt-24"
            >
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-indigo-400/60 to-transparent" />
                <span className="flex items-center gap-2 whitespace-nowrap rounded-full border border-indigo-200 bg-indigo-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-indigo-600 shadow-sm dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-300">
                    <CalendarDays className="h-4 w-4" />
                    Earnings Radar
                </span>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent via-indigo-400/60 to-transparent" />
            </div>

            <section className="pb-12">
                <EarningsWidget />
            </section>

            {/* --- Top Performers: eye-catching separator + widget --------- */}
            {/* Owns its own TanStack Query state, so it renders independently of
                the momentum leaderboards and earnings board above. */}
            <div
                id="top-performers"
                className="mt-16 mb-10 flex items-center gap-4 scroll-mt-24"
            >
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
                <span className="flex items-center gap-2 whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-amber-600 shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                    <Flame className="h-4 w-4" />
                    Top Performers
                </span>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent via-amber-400/60 to-transparent" />
            </div>

            <section className="pb-12">
                <TopPerformersWidget />
            </section>
        </Layout>
    );
};

export default BreakoutInsightsPage;
